import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { completeService, getService } from '../../api/servicesAPI';
import { getCurrentUser } from '../../api/usersAPI';
import { formatDateTime, formatDecimal, formatMileage } from '../../utils/dateUtils';
import { formatPhoneNum } from '../../utils/phoneUtils';
import PrintMenu from '../layout/PrintMenu';
import TopazSigWebPad from '../signature/TopazSigWebPad';

export default function ViewService() {
  const { sid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [service, setService] = useState({});
  const [currentUserName, setCurrentUserName] = useState('');
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const [completeServiceRecord, setCompleteServiceRecord] = useState(null);
  const [isCompleteDialogLoading, setIsCompleteDialogLoading] = useState(false);
  const [isCompletingService, setIsCompletingService] = useState(false);
  const [completionError, setCompletionError] = useState('');
  const [completionPaymentType, setCompletionPaymentType] = useState('');
  const [completionSignaturePayload, setCompletionSignaturePayload] = useState(null);
  const [shouldAutoOpenCompleteDialog, setShouldAutoOpenCompleteDialog] = useState(false);
  const signaturePadRef = useRef(null);
  const isCompleted = String(service.status ?? '').trim().toLowerCase() === 'completed';

  const PAYMENT_TYPE_OPTIONS = [
    'Cash',
    'Card',
    'Check',
    'Warranty',
    'No Charge',
    'Other',
  ];

  const boldStyle = {
    fontWeight:'bold',
  }

  const boldRightAlignStyle = {
    fontWeight:'bold',
    textAlign:'right',
  }

  const branch = service.branch_details;
  const serviceOwner = service.service_owner_details ?? service.service_owner;
  const taxExempt = serviceOwner?.tax_exempt ?? false;
  const vehicle = service.vehicle;
  const customerNotes = service.customer_notes?.trim();
  const signedAt = service.customer_signed_at ?? service.completed_at;
  const storedStaffName = service.completed_by_name?.trim();
  const staffDisplayName = storedStaffName || currentUserName || '____________________';
  const serviceOwnerName = serviceOwner?.name
    ? `${serviceOwner.name}${taxExempt ? ' (Tax Exempt)' : ''}`
    : 'n/a';
  const printedOwnerName = serviceOwner?.name || '_________________________________';
  const paymentTypeDisplay = service.payment_type || '____________________';
  const arrivalTypeDisplay =
    service.arrival_type === 'drop_off'
      ? 'Drop off'
      : service.arrival_type === 'stay'
        ? 'Stay'
        : 'Unspecified';
  const returnTo = location.state?.returnTo || { pathname: vehicle?.id ? `/vehicles/${vehicle.id}` : '/' };

  const warnIfCompletedService = (serviceRecord) => {
    const status = String(serviceRecord?.status ?? '').trim().toLowerCase();
    if (status === 'completed') {
      const confirmed = window.confirm(
        `This service has already been marked as completed.\n\n` +
        `Editing will delete the signature.`
      );
      return !confirmed;
    }
    return false;
  };

  const handleCloseCompleteDialog = (shouldReturn = false) => {
    setIsCompleteDialogOpen(false);
    setCompleteServiceRecord(null);
    setIsCompleteDialogLoading(false);
    setIsCompletingService(false);
    setCompletionError('');
    setCompletionPaymentType('');
    setCompletionSignaturePayload(null);

    if (shouldReturn) {
      if (typeof returnTo === 'string') {
        navigate(returnTo, { replace: true });
        return;
      }

      navigate(returnTo.pathname, {
        replace: true,
        state: returnTo.state,
      });
    }
  };

  const handleOpenCompleteDialog = useCallback(async () => {
    if (!service?.id) {
      return;
    }

    if (warnIfCompletedService(service)) {
      return;
    }

    setIsCompleteDialogOpen(true);
    setIsCompleteDialogLoading(true);
    setIsCompletingService(false);
    setCompletionError('');
    setCompletionSignaturePayload(null);
    setCompletionPaymentType(service.payment_type || '');

    try {
      const response = await getService(service.id);
      const completeData = response.data;
      setCompleteServiceRecord(completeData);
      setCompletionPaymentType(completeData.payment_type || '');
    } catch (error) {
      setCompleteServiceRecord(service);
      setCompletionPaymentType(service.payment_type || '');
      setCompletionError('Unable to load the service completion form.');
      console.error(error);
    } finally {
      setIsCompleteDialogLoading(false);
    }
  }, [service]);

  const handleCompleteService = async () => {
    if (!completeServiceRecord) {
      return;
    }

    if (!completionPaymentType) {
      setCompletionError('Select a payment type before completing the service.');
      return;
    }

    if (!signaturePadRef.current && !completionSignaturePayload) {
      setCompletionError('Topaz signature pad is not ready.');
      return;
    }

    setIsCompletingService(true);
    setCompletionError('');

    try {
      let signaturePayload = completionSignaturePayload;

      if (!signaturePayload) {
        signaturePayload = await signaturePadRef.current.getSignatureData();
        setCompletionSignaturePayload(signaturePayload);
      }

      await completeService(completeServiceRecord.id, {
        customer_signature_image: signaturePayload.imageDataUrl,
        customer_signature_sig_string: signaturePayload.sigString,
        payment_type: completionPaymentType,
      });

      handleCloseCompleteDialog(false);
      const refreshedService = await getService(service.id);
      setService(refreshedService.data);
      navigate('/services/' + service.id, {
        replace: true,
        state: {
          returnTo,
        },
      });
    } catch (error) {
      const responseData = error.response?.data;
      const errorMessage =
        responseData?.detail ||
        responseData?.payment_type?.[0] ||
        responseData?.customer_signature_image?.[0] ||
        error.message ||
        'Unable to complete the service.';

      setCompletionError(errorMessage);
      console.error(error);
    } finally {
      setIsCompletingService(false);
    }
  };

  useEffect(() => {
    async function fetchService() {
      try {
        const response = await getService(sid);
        setService(response.data);
      } catch (error) {
        console.error(error);
      }
    }

    async function fetchCurrentUser() {
      try {
        const response = await getCurrentUser();
        setCurrentUserName(response.data.full_name ?? '');
      } catch (error) {
        console.error(error);
      }
    }

    fetchService();
    fetchCurrentUser();
  }, [sid]);

  useEffect(() => {
    if (location.state?.openCompleteDialog) {
      setShouldAutoOpenCompleteDialog(true);
      navigate(location.pathname, {
        replace: true,
        state: {
          returnTo: location.state.returnTo,
        },
      });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!shouldAutoOpenCompleteDialog || !service?.id || isCompleteDialogOpen) {
      return;
    }

    setShouldAutoOpenCompleteDialog(false);
    handleOpenCompleteDialog();
  }, [handleOpenCompleteDialog, isCompleteDialogOpen, service, shouldAutoOpenCompleteDialog]);

  return (
    <>
      <PrintMenu
        previewText='Viewing Final Service Invoice'
        fallbackTo={returnTo}
        onComplete={handleOpenCompleteDialog}
        showCompleteButton={Boolean(service?.id) && !isCompleted}
      />
      <Box sx={{textAlign: 'center', margin: '-25px auto 0', width: '670px'}}>
        <Typography variant='h1' sx={{fontSize: '28px', mb:1}}>SERVICE INVOICE</Typography>
        {branch?.business_name && <Typography sx={{fontSize: '16px', fontWeight: 600}}>{branch.business_name}</Typography>}
        {branch?.name && <Typography sx={{fontSize: '14px'}}>{branch.name}</Typography>}
        {branch?.address && <Typography sx={{fontSize: '14px'}}>{branch.address}</Typography>}
        {branch?.phone && <Typography sx={{fontSize: '14px'}}>{formatPhoneNum(branch.phone)}</Typography>}
        <Box sx={{display:'flex', justifyContent:'space-between', my:1}}>
          <Box sx={{flex: 1}}>
            <Typography fontSize='.85rem'><b>Last Updated</b></Typography>
            <Typography fontSize='.85rem'>{formatDateTime(service.updated_at)}</Typography>
          </Box>
          <Box sx={{flex: 1}}>
            <Typography fontSize='.85rem'><b>Year/Make/Model</b></Typography>
            {vehicle?.short_description && <Typography fontSize='.85rem'>{vehicle.short_description}</Typography>}
          </Box>
          <Box sx={{flex: 1}}>
            <Typography fontSize='.85rem'><b>VIN #</b></Typography>
            <Typography fontSize='.85rem'>{vehicle?.vin ?? 'N/A'}</Typography>
          </Box>
          {service.mileage != null && service.mileage !== '' && (
            <Box sx={{flex: 1}}>
              <Typography fontSize='.85rem'><b>Mileage</b></Typography>
              <Typography fontSize='.85rem'>{formatMileage(service.mileage)}</Typography>
            </Box>
          )}
        </Box>
        {serviceOwner?.name && (
          <Typography sx={{my:2, fontSize: '14px'}}>
            Prepared for: {serviceOwnerName}
          </Typography>
        )}
        <Typography sx={{mb:2, fontSize: '14px'}}>
          Drop Off / Stay: {arrivalTypeDisplay}
        </Typography>
        <Typography variant='h2' sx={{fontSize: '20px'}}>Service Information</Typography>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={boldStyle}>DESCRIPTION</TableCell>
                <TableCell sx={boldRightAlignStyle}>QTY</TableCell>
                <TableCell sx={boldRightAlignStyle}>PARTS</TableCell>
                <TableCell sx={boldRightAlignStyle}>LABOR</TableCell>
                <TableCell sx={boldRightAlignStyle}>TOTAL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {service.service_items && service.service_items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.description}</TableCell>
                  <TableCell sx={{textAlign:'right'}}>{item.quantity}</TableCell>
                  <TableCell sx={{textAlign:'right'}}>${formatDecimal(item.part_price)}</TableCell>
                  <TableCell sx={{textAlign:'right'}}>${formatDecimal(item.labor_price)}</TableCell>
                  <TableCell sx={{textAlign:'right'}}>${formatDecimal(item.service_item_total)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={boldStyle}>SUB-TOTALS</TableCell>
                <TableCell sx={boldRightAlignStyle}></TableCell>
                <TableCell sx={boldRightAlignStyle}>${formatDecimal(service.parts_total)}</TableCell>
                <TableCell sx={boldRightAlignStyle}>${formatDecimal(service.labor_total)}</TableCell>
                <TableCell sx={boldRightAlignStyle}>${formatDecimal(service.service_subtotal)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={boldStyle}>SALES TAX ({taxExempt ? 'Exempt' : '5.5%'})</TableCell>
                <TableCell sx={boldRightAlignStyle}></TableCell>
                <TableCell sx={boldRightAlignStyle}></TableCell>
                <TableCell sx={boldRightAlignStyle}></TableCell>
                <TableCell sx={boldRightAlignStyle}>${formatDecimal(service.sales_tax_total)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={boldStyle}>SERVICE TOTAL</TableCell>
                <TableCell sx={boldRightAlignStyle}></TableCell>
                <TableCell sx={boldRightAlignStyle}></TableCell>
                <TableCell sx={boldRightAlignStyle}></TableCell>
                <TableCell sx={boldRightAlignStyle}>${formatDecimal(service.service_total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {customerNotes && (
          <Box sx={{border: '2px solid black', padding: '10px', marginTop: '16px', textAlign: 'left'}}>
            <Typography sx={{fontSize: '14px', fontWeight: 600}}>Customer Notes</Typography>
            <Typography sx={{fontSize: '12px', marginTop: '8px', whiteSpace: 'pre-wrap'}}>{customerNotes}</Typography>
          </Box>
        )}

        <Box sx={{border: '2px solid black', padding: '10px', marginTop: '16px', textAlign: 'left'}}>
          <Typography sx={{fontSize: '14px', fontWeight: 600}}>Customer Authorization</Typography>
          <Typography sx={{fontSize: '12px', marginTop: '8px'}}>
            This price for the authorized repairs will not be exceeded if the motor vehicle is delivered to the shop
            within 5 days. You are entitled to a price estimate for the repairs you have authorized. The repair price
            may be less than the estimate, but will not exceed the estimate without your permission.
          </Typography>
          <Typography sx={{fontSize: '12px', marginTop: '8px'}}>
            Motor vehicle repair practices are regulated by chapter ATCP 132, Wis. Adm. Code, administered by the
            Bureau of Consumer Protection, Wisconsin Dept. of Agriculture, Trade and Consumer Protection, P.O. Box
            8911, Madison, Wisconsin 53708-8911.
          </Typography>
          <Typography sx={{fontSize: '12px', marginTop: '8px'}}>Customer signature:</Typography>
          {service.customer_signature_image ? (
            <Box
              component='img'
              src={service.customer_signature_image}
              alt='Customer signature'
              sx={{
                display: 'block',
                marginTop: '8px',
                maxWidth: '240px',
                maxHeight: '80px',
                borderBottom: '1px solid black',
              }}
            />
          ) : (
            <Typography sx={{fontSize: '12px', marginTop: '8px'}}>____________________________</Typography>
          )}
          <Typography sx={{fontSize: '12px', marginTop: '8px'}}>Printed name: {printedOwnerName}</Typography>
          <Typography sx={{fontSize: '12px', marginTop: '8px'}}>Date: {signedAt ? formatDateTime(signedAt) : '____________________'}</Typography>
        </Box>
        <Box sx={{border: '2px solid black', padding: '10px', marginTop: '16px', textAlign: 'left'}}>
          <Typography sx={{fontSize: '14px', fontWeight: 600}}>Staff Confirmation</Typography>
          <Box sx={{display: 'flex', gap: '50px', marginTop: '8px'}}>
            <Typography sx={{flex: 1, fontSize: '12px'}}>Staff: {staffDisplayName}</Typography>
            <Typography sx={{flex: 1, fontSize: '12px'}}>Payment type: {paymentTypeDisplay}</Typography>
          </Box>
        </Box>
      </Box>

      <Dialog open={isCompleteDialogOpen} onClose={() => handleCloseCompleteDialog(true)} maxWidth='md' fullWidth>
        <DialogTitle>Complete Service Invoice</DialogTitle>
        <DialogContent dividers>
          {isCompleteDialogLoading && <Typography>Loading…</Typography>}
          {completionError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {completionError}
            </Alert>
          )}
          {!isCompleteDialogLoading && completeServiceRecord && (
            <Box>
              <Typography variant='subtitle1'>
                {completeServiceRecord.vehicle?.short_description || 'Vehicle'}
              </Typography>
              <Typography sx={{ fontSize: '12px', mb: 2 }}>
                {completeServiceRecord.service_owner_details?.name || completeServiceRecord.vehicle?.list_owners_str || 'Customer'}
                {' '}• {completeServiceRecord.service_items_str}
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2 }}>
                Total: ${formatDecimal(completeServiceRecord.service_total)}
              </Typography>
              <Typography variant='subtitle2'>Customer Signature</Typography>
              <TopazSigWebPad
                ref={signaturePadRef}
                onCleared={() => setCompletionSignaturePayload(null)}
              />
              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel id='completion-payment-type-label'>Payment Type</InputLabel>
                <Select
                  labelId='completion-payment-type-label'
                  value={completionPaymentType}
                  label='Payment Type'
                  onChange={(event) => setCompletionPaymentType(event.target.value)}
                >
                  {PAYMENT_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseCompleteDialog(true)}>Cancel</Button>
          <Button
            onClick={handleCompleteService}
            variant='contained'
            disabled={isCompletingService || isCompleteDialogLoading}
          >
            {isCompletingService ? 'Completing…' : 'Complete Service'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}